import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert, Chip, CircularProgress, Stack, IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { colorTaxonomyApi } from '../../services/api';

export default function ColorFamiliesPanel() {
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ family: '', description: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await colorTaxonomyApi.listFamilies();
      setFamilies(resp.families || []);
    } catch (e) {
      setError(e.message || 'Failed to load families');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleOpenAdd = () => {
    setEditing(null);
    setFormData({ family: '', description: '' });
    setDialogOpen(true);
  };

  const handleOpenEdit = (f) => {
    setEditing(f);
    setFormData({ family: f.family, description: f.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      if (editing) {
        await colorTaxonomyApi.updateFamily(editing.id, formData);
      } else {
        await colorTaxonomyApi.createFamily(formData);
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (f) => {
    if (f.color_count > 0) {
      window.alert(
        `Cannot delete "${f.family}" — ${f.color_count} normalized colors still reference it. ` +
        `Reassign them first (in the Color → Family sub-tab).`
      );
      return;
    }
    if (!window.confirm(`Delete color family "${f.family}"?`)) return;
    try {
      setError(null);
      await colorTaxonomyApi.deleteFamily(f.id);
      await load();
    } catch (e) {
      setError(e.message || 'Failed to delete');
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Stack direction="row" spacing={1} sx={{ mb: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Canonical color families. Used by hub-transitive matching to determine compatible colors
          (blue ↔ blue, red ↔ red, metallic ↔ metallic). Adding a family makes it available in the
          "Color → Family" dropdown.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>Add Family</Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Family</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell align="right"><strong>Colors</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {families.map(f => (
              <TableRow key={f.id}>
                <TableCell><Chip label={f.family} size="small" color="primary" /></TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{f.description || '—'}</Typography></TableCell>
                <TableCell align="right">{f.color_count}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpenEdit(f)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => handleDelete(f)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Color Family' : 'Add Color Family'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Family name"
            value={formData.family}
            onChange={(e) => setFormData({ ...formData, family: e.target.value })}
            fullWidth margin="normal"
            helperText="Lowercase, e.g. blue, gray, metallic, neutral"
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            fullWidth margin="normal" multiline rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !formData.family}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
